'use client';

import { useRef, useState, useEffect } from 'react';
import Link from 'next/link';

export default function Consent({
  setShowModal,
  onAccept,
}: {
  setShowModal: (show: boolean) => void;
  onAccept?: () => void;
}) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [hasScrolled, setHasScrolled] = useState(false);
  const [checkbox1, setCheckbox1] = useState(false);
  const [checkbox2, setCheckbox2] = useState(false);
  const [checkbox3, setCheckbox3] = useState(false);

  const allChecked = checkbox1 && checkbox2 && checkbox3;

  const handleScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    const atBottom = el.scrollHeight - el.scrollTop <= el.clientHeight + 50;
    if (atBottom) setHasScrolled(true);
  };

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    el.addEventListener('scroll', handleScroll);
    return () => el.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
      <div className="bg-white rounded-xl shadow-2xl max-w-3xl w-full mx-4 relative flex flex-col max-h-[90vh]">

          {/* Header */}
          <div className="px-6 pt-6 pb-3 border-b border-gray-200 relative">
            <button
              onClick={() => setShowModal(false)}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 text-2xl font-light focus:outline-none"
            >
              &times;
            </button>
            <h2 className="text-2xl font-light text-[#004C73]">
              Lingwell User Agreement, Privacy Policy & Terms of Use
            </h2>
            <p className="text-sm text-gray-500 mt-1">
              Please scroll through the entire document before accepting.
            </p>
          </div>
        {/* Scrollable Content */}
        <div
          ref={scrollRef}
          className="overflow-y-auto flex-1 px-6 py-4 space-y-6 text-gray-800 text-sm leading-relaxed"
        >
          {/* User Agreement */}
          <div>
            <h3 className="text-xl font-light text-[#004C73] mb-1">User Agreement and Pilot Consent</h3>
            <p className="text-xs text-gray-500 mb-3">Effective Date: May 19, 2026</p>
            <p className="font-semibold text-xs mb-3">IMPORTANT: PLEASE READ CAREFULLY. THIS AGREEMENT CONTAINS A MANDATORY ARBITRATION PROVISION AND CLASS ACTION WAIVER.</p>
            <p className="mb-3">By clicking &ldquo;I Agree&rdquo; or by accessing or using Lingwell, you enter into a legally binding agreement with Center for Linguistic Health LLC. If you do not agree, do not use Lingwell.</p>

            <p className="text-base font-bold text-[#004C73] mt-4"><strong>1. Nature of the Service</strong></p>
            <p className="mb-2">Lingwell is an AI-guided reflective writing tool currently offered in pilot form. It is designed for structured self-expression and personal reflection. It is not medical care, psychotherapy, psychiatric treatment, crisis intervention, or a substitute for professional diagnosis or treatment. Lingwell does not create a therapist-client, physician-patient, fiduciary, or other professional relationship. Lingwell is not a regulated medical device.</p>

            <p className="text-base font-bold text-[#004C73] mt-4"><strong>2. AI-Generated Content</strong></p>
            <p className="mb-2">Lingwell generates responses through automated artificial intelligence systems. Responses are algorithmically generated, not reviewed in real time by licensed professionals, and may be inaccurate, incomplete, or inappropriate. Outputs are not medical or psychological advice. You assume full responsibility for how you interpret and use any system output.</p>

            <p className="text-base font-bold text-[#004C73] mt-4"><strong>3. No Duty to Monitor or Intervene</strong></p>
            <p className="mb-2">Lingwell does not monitor user activity in real time and does not reliably detect crisis signals or emergency conditions. Lingwell has no obligation to monitor, respond to, or intervene in situations involving distress, self-harm, harm to others, or emergency circumstances.</p>

            <p className="text-base font-bold text-[#004C73] mt-4"><strong>4. Crisis Use Limitation</strong></p>
            <p className="mb-2">Lingwell is not designed to identify, assess, or respond to mental health crises or emergency situations. If you are in crisis, experiencing suicidal thoughts, or require immediate assistance, call 911, contact local emergency services, or contact the 988 Suicide and Crisis Lifeline immediately.</p>

            <p className="text-base font-bold text-[#004C73] mt-4"><strong>5. Eligibility</strong></p>
            <p className="mb-2">You must be at least 18 years of age to use Lingwell. By agreeing to this Agreement, you represent and warrant that you are 18 years of age or older and legally capable of entering into binding contracts.</p>

            <p className="text-base font-bold text-[#004C73] mt-4"><strong>6. Pilot Participation</strong></p>
            <p className="mb-2">Lingwell is currently in a pilot phase. Participation is voluntary. You may discontinue use at any time. Lingwell may suspend or terminate access at its sole discretion.</p>

            <p className="text-base font-bold text-[#004C73] mt-4"><strong>7. Information We Collect</strong></p>
            <p className="mb-1">During your use of Lingwell, we may collect:</p>
            <ul className="list-disc list-inside mb-2 space-y-1">
              <li>Text you enter</li>
              <li>Lingwell-generated responses</li>
              <li>Timestamps and session duration</li>
              <li>Device, browser, IP address, and technical metadata</li>
              <li>Optional feedback</li>
            </ul>

            <p className="text-base font-bold text-[#004C73] mt-4"><strong>8. Use of Information</strong></p>
            <p className="mb-2">We use collected information to operate and maintain the service, improve system clarity, safety, and usability, and conduct internal analytics. We do not use identifiable personal content for advertising, resale, or training publicly available AI models.</p>

            <p className="text-base font-bold text-[#004C73] mt-4"><strong>9. User Content and License</strong></p>
            <p className="mb-2">You retain ownership of content you submit. By using Lingwell, you grant Center for Linguistic Health LLC a non-exclusive, worldwide, royalty-free license to use, process, and analyze your content solely for operating and improving the service.</p>

            <p className="text-base font-bold text-[#004C73] mt-4"><strong>10. Health Information Disclaimer</strong></p>
            <p className="mb-2">Lingwell is not intended for the storage or transmission of protected health information unless expressly agreed to in a separate written agreement.</p>

            <p className="text-base font-bold text-[#004C73] mt-4"><strong>11. Data Security and Retention</strong></p>
            <p className="mb-2">We implement commercially reasonable safeguards including encryption and access controls. Identifiable user content is retained only as long as necessary to provide the service. You may request deletion of identifiable data through the website.</p>

            <p className="text-base font-bold text-[#004C73] mt-4"><strong>12. Assumption of Risk</strong></p>
            <p className="mb-2">You acknowledge that engaging in written reflection about personal or emotional experiences may involve discomfort or unintended emotional responses. You voluntarily assume all risks associated with your use of Lingwell.</p>

            <p className="text-base font-bold text-[#004C73] mt-4"><strong>13. Disclaimer of Warranties</strong></p>
            <p className="mb-2">Lingwell is provided on an &ldquo;as is&rdquo; and &ldquo;as available&rdquo; basis. To the fullest extent permitted by law, Center for Linguistic Health LLC disclaims all warranties. No guarantee is made regarding outcomes, emotional benefit, clarity, or personal improvement.</p>

            <p className="text-base font-bold text-[#004C73] mt-4"><strong>14. Limitation of Liability</strong></p>
            <p className="mb-2">To the fullest extent permitted by law, Center for Linguistic Health LLC shall not be liable for any indirect, incidental, consequential, special, exemplary, or punitive damages. Total liability shall not exceed $100 or the total amount paid in the twelve months preceding the claim.</p>

            <p className="text-base font-bold text-[#004C73] mt-4"><strong>15. Governing Law & Arbitration</strong></p>
            <p className="mb-2">This Agreement is governed by the laws of the State of Tennessee. Any dispute shall be resolved through binding arbitration in Tennessee on an individual basis, waiving any right to class action.</p>

            <p className="text-base font-bold text-[#004C73] mt-4"><strong>16. Updates</strong></p>
            <p className="mb-2">We may update this Agreement from time to time. Continued use of Lingwell after updates constitutes acceptance of revised terms.</p>

            <p className="text-base font-bold text-[#004C73] mt-4"><strong>17. Contact</strong></p>
            <p className="mb-4">For questions or data requests, please contact us through{' '}
              <a href="https://www.lingwellhealth.com" target="_blank" rel="noopener noreferrer" className="text-[#004C73] underline">www.lingwellhealth.com</a>.
            </p>
          </div>

          {/* Divider */}
          <hr className="border-gray-300" />

          {/* Privacy Policy */}
          <div>
            <h3 className="text-xl font-light text-[#004C73] mb-1">Privacy Policy</h3>
            <p className="text-xs text-gray-500 mb-3">Effective Date: May 19, 2026</p>

            <p className="text-base font-bold text-[#004C73] mt-4"><strong>1. Overview</strong></p>
            <p className="mb-2">Lingwell is a guided, AI-supported reflection tool. This Privacy Policy explains how Lingwell collects, uses, stores, and protects information when you use the product. By using Lingwell, you agree to the practices described in this Policy.</p>

            <p className="text-base font-bold text-[#004C73] mt-4"><strong>2. The Nature of the Service</strong></p>
            <p className="mb-2">Lingwell processes user-provided text using automated AI systems to generate reflective responses. It is not a medical, psychological, or clinical service and is not intended to diagnose, treat, or manage health conditions.</p>

            <p className="text-base font-bold text-[#004C73] mt-4"><strong>3. Information We Collect</strong></p>
            <ul className="list-disc list-inside mb-2 space-y-1">
              <li>Text you enter during sessions</li>
              <li>Lingwell-generated responses and summaries</li>
              <li>Session timestamps and duration</li>
              <li>Device, browser, and technical metadata</li>
              <li>Optional feedback you choose to provide</li>
            </ul>

            <p className="text-base font-bold text-[#004C73] mt-4"><strong>4. How Information Is Used</strong></p>
            <p className="mb-2">We use information to deliver and operate the service, maintain system performance, improve clarity and usability, and conduct internal analytics. Lingwell does not use identifiable personal content for advertising, resale, or training publicly available AI models.</p>

            <p className="text-base font-bold text-[#004C73] mt-4"><strong>5. Data Sharing and Disclosure</strong></p>
            <p className="mb-2">Lingwell does not sell, rent, or lease user data. Information may be shared with trusted service providers solely to operate the service, or when required by law.</p>

            <p className="text-base font-bold text-[#004C73] mt-4"><strong>6. Data Security</strong></p>
            <p className="mb-2">Lingwell uses commercially reasonable safeguards including encryption in transit and at rest. No system can guarantee absolute security.</p>

            <p className="text-base font-bold text-[#004C73] mt-4"><strong>7. User Control and Sharing</strong></p>
            <p className="mb-2">Users control whether to share Lingwell-generated summaries. Lingwell does not automatically share user content or summaries with third parties.</p>

            <p className="text-base font-bold text-[#004C73] mt-4"><strong>8. Data Retention</strong></p>
            <p className="mb-2">Lingwell retains identifiable user data only as long as necessary to provide the service. Users may request deletion through Lingwell&apos;s website.</p>

            <p className="text-base font-bold text-[#004C73] mt-4"><strong>9. Children&apos;s Privacy</strong></p>
            <p className="mb-2">Lingwell is intended for users 18 years of age or older. Lingwell does not knowingly collect personal information from individuals under 18.</p>

            <p className="text-base font-bold text-[#004C73] mt-4"><strong>10. Contact</strong></p>
            <p className="mb-4">For questions, please contact Center for Linguistic Health LLC via{' '}
              <a href="https://www.lingwellhealth.com" target="_blank" rel="noopener noreferrer" className="text-[#004C73] underline">www.lingwellhealth.com</a>.
            </p>
          </div>

          {/* Divider */}
          <hr className="border-gray-300" />

          {/* Terms of Use */}
          <div>
            <h3 className="text-xl font-light text-[#004C73] mb-1">Terms of Use</h3>
            <p className="text-xs text-gray-500 mb-3">Effective Date: May 19, 2026</p>

            <p className="text-base font-bold text-[#004C73] mt-4"><strong>1. Acceptance of Terms</strong></p>
            <p className="mb-2">By accessing or using the Lingwell website, you agree to these Terms of Use. If you do not agree, you should not use the Site.</p>

            <p className="text-base font-bold text-[#004C73] mt-4"><strong>2. Purpose of the Site</strong></p>
            <p className="mb-2">The Site provides general information about Lingwell and its services for informational purposes only. It does not constitute medical, psychological, or professional advice.</p>

            <p className="text-base font-bold text-[#004C73] mt-4"><strong>3. No Professional Relationship</strong></p>
            <p className="mb-2">Use of the Site does not create any therapist-client, physician-patient, fiduciary, or other professional relationship.</p>

            <p className="text-base font-bold text-[#004C73] mt-4"><strong>4. Intellectual Property</strong></p>
            <p className="mb-2">All content on the Site is the property of Center for Linguistic Health LLC. You may not copy, reproduce, distribute, or modify Site content without prior written permission.</p>

            <p className="text-base font-bold text-[#004C73] mt-4"><strong>5. Disclaimer of Warranties</strong></p>
            <p className="mb-2">The Site is provided on an &ldquo;as is&rdquo; and &ldquo;as available&rdquo; basis. Center for Linguistic Health LLC disclaims all warranties to the fullest extent permitted by law.</p>

            <p className="text-base font-bold text-[#004C73] mt-4"><strong>6. Limitation of Liability</strong></p>
            <p className="mb-2">To the fullest extent permitted by law, Center for Linguistic Health LLC shall not be liable for any damages arising from your use of the Site.</p>

            <p className="text-base font-bold text-[#004C73] mt-4"><strong>7. Governing Law</strong></p>
            <p className="mb-2">These Terms are governed by the laws of the State of Tennessee.</p>

           <p className="text-base font-bold text-[#004C73] mt-4"><strong>8. Contact</strong></p>
<p className="mb-2">For questions, please contact Center for Linguistic Health LLC via <a href="mailto:support@lingwellhealth.com" className="text-blue-600 underline hover:text-blue-800">support@lingwellhealth.com</a> or <a href="https://www.lingwellhealth.com" target="_blank" rel="noopener noreferrer" className="text-[#004C73] underline">www.lingwellhealth.com</a>.</p>
          </div>

          {/* Scroll indicator */}
          {!hasScrolled && (
            <p className="text-center text-xs text-gray-400 animate-bounce pb-2">
              ↓ Please scroll to the bottom to continue
            </p>
          )}
        </div>

        {/* Footer - 3 Checkboxes + Button */}
        <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 rounded-b-xl space-y-3">

          {/* Checkbox 1 */}
          <label className={`flex items-start gap-3 text-sm ${!hasScrolled ? 'opacity-40 cursor-not-allowed' : 'cursor-pointer'}`}>
            <input
              type="checkbox"
              disabled={!hasScrolled}
              checked={checkbox1}
              onChange={(e) => setCheckbox1(e.target.checked)}
              className="mt-1 accent-[#024d78] w-4 h-4 flex-shrink-0"
            />
            <span className="text-[#004C73] leading-snug">
              I agree to Lingwell&apos;s{' '}
              <Link href="/user-agreement" target="_blank" className="underline hover:text-[#22566c]">
                User Agreement
              </Link>
            </span>
          </label>

          {/* Checkbox 2 */}
          <label className={`flex items-start gap-3 text-sm ${!hasScrolled ? 'opacity-40 cursor-not-allowed' : 'cursor-pointer'}`}>
            <input
              type="checkbox"
              disabled={!hasScrolled}
              checked={checkbox2}
              onChange={(e) => setCheckbox2(e.target.checked)}
              className="mt-1 accent-[#024d78] w-4 h-4 flex-shrink-0"
            />
            <span className="text-[#004C73] leading-snug">
              I acknowledge Lingwell&apos;s{' '}
              <Link href="/privacy-policy" target="_blank" className="underline hover:text-[#22566c]">
                Privacy Policy
              </Link>
            </span>
          </label>

          {/* Checkbox 3 */}
          <label className={`flex items-start gap-3 text-sm ${!hasScrolled ? 'opacity-40 cursor-not-allowed' : 'cursor-pointer'}`}>
            <input
              type="checkbox"
              disabled={!hasScrolled}
              checked={checkbox3}
              onChange={(e) => setCheckbox3(e.target.checked)}
              className="mt-1 accent-[#024d78] w-4 h-4 flex-shrink-0"
            />
            <span className="text-[#004C73] leading-snug">
              I understand Lingwell is a tool for reflection and personal insight. It is not therapy, crisis support, or medical care. If you are experiencing a crisis, call 988 or contact emergency services. By continuing, you confirm that you are at least 18 years old.
            </span>
          </label>

          {/* Continue Button */}
          <button
            disabled={!allChecked}
            onClick={() => {
              setShowModal(false);
              onAccept?.();
            }}
            className={`w-full sm:w-[200px] md:w-[300px] text-center px-6 py-2 text-sm rounded-full font-light text-white shadow-md transition-transform albert-font mt-2
              ${allChecked ? 'hover:scale-[1.02] cursor-pointer' : 'opacity-40 cursor-not-allowed'}`}
            style={{
              background: 'linear-gradient(180deg, #67C17E 0%, #3FA057 100%)',
            }}
          >
            Continue
          </button>
        </div>
      </div>
    </div>
  );
}
